import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { fetchInstallationsStats, getInstallations, getInstallationsByDeviceId } from '../api/installation.api'
import { Installation } from '../models/installation.model'
import { isUndefined } from 'lodash'
import { useEffect, useState } from 'react'
import { useUser } from 'src/Hooks/useUser'

interface UseInstallationHook {
  getInstallationBySerialNumber: (deviceId: number) => Promise<Installation | undefined>
  getAllInstallations: () => Promise<Installation[]>
  getInstallationsStats: () => Promise<number>
  installations: Installation[]
  installationCount: number
  installationsLoading: boolean
  refreshInstallations: () => Promise<void>
}

/*
 * This Hook provides functions for handeling installations
 */
const useInstallation = (): UseInstallationHook => {
  const authFetch = useAuthFetchWithResponse()
  const [installationAmount, setInstallationAmount] = useState<number>(-1)
  const [installations, setInstallations] = useState<Installation[]>([])
  const [installationsLoading, setInstallationsLoading] = useState<boolean>(true)
  const [installationCountLoading, setInstallationCountLoading] = useState<boolean>(true)
  const { userData } = useUser()

  const refreshInstallations = async (): Promise<void> => {
    setInstallationsLoading(true)
    setInstallationCountLoading(true)

    await Promise.all([
      getInstallationsStats()
        .then(count => {
          setInstallationAmount(count)
        })
        .catch(error => console.error(error))
        .finally(() => setInstallationCountLoading(false)),
      getAllInstallations()
        .then(installationsData => {
          setInstallations(installationsData)
        })
        .catch(error => console.error(error))
        .finally(() => setInstallationsLoading(false)),
    ])
  }

  useEffect(() => {
    if (userData?.level !== 'USER') return
    getInstallationsStats()
      .then(count => {
        setInstallationAmount(count)
      })
      .catch(error => console.error(error))
      .finally(() => setInstallationCountLoading(false))
  }, [userData]) //eslint-disable-line

  useEffect(() => {
    if (isUndefined(userData)) return
    if (userData?.level !== 'USER') {
      setInstallationsLoading(false)
      return
    }
    if (installationCountLoading) return
    if (installationAmount <= 4 && installationAmount !== 0) {
      getAllInstallations()
        .then(installationsData => {
          setInstallations(installationsData)
        })
        .catch(error => console.error(error))
        .finally(() => setInstallationsLoading(false))
    } else {
      setInstallationsLoading(false)
    }
  }, [installationCountLoading, userData]) //eslint-disable-line

  const getInstallationBySerialNumber = async (deviceId: number): Promise<Installation | undefined> => {
    const result = await getInstallationsByDeviceId(deviceId, authFetch)
    if (result.hasErrors) return
    if (!result.value) return
    if (isUndefined(result.value[0])) return
    return result.value[0]
  }

  const getAllInstallations = async (): Promise<Installation[]> => {
    const result = await getInstallations(authFetch)
    if (result.hasErrors) return []
    if (!result.value) return []
    return result.value
  }

  const getInstallationsStats = async (): Promise<number> => {
    const result = await fetchInstallationsStats(authFetch)
    if (result.hasErrors) return 0
    if (!result.value) return 0
    return result.value
  }

  return {
    getInstallationBySerialNumber,
    getInstallationsStats,
    getAllInstallations,
    installations,
    installationCount: installationAmount,
    installationsLoading,
    refreshInstallations,
  }
}

export default useInstallation
